import assertPayload from "./assert-payload";
import { domComponentPath } from "@swissfederalrailways/dom-component-path";
import { querySelectorDeep } from "./query-selector";
import {
  PageLoadedParameters,
  DataLayerEntry,
  SearchLoadedParameters,
  TrackAccordionParameters,
  ComponentPath,
  NavigationParameters,
  CustomEventParameters,
  UserLoginTypeParameters,
  TrackSearchResultNavigationParameters,
  TrackDownloadParameters,
  TrackImpressionParameters,
  TrackTeaserNavigationParameters,
  TrackTeaserImpressionParameters,
  TrackCustomNavigationParameters,
  EventParameters,
} from "./types";
import {
  setEventCategory,
  setEventInfo,
  setPageAttributes,
  setUserProfileInfo,
} from "./utils";

export const REDIRECTION_TIMEOUT = 300;

window.digitalDataLayer = window.digitalDataLayer || [];
let callbackIsPending = false;

function pushToDataLayer(entry: DataLayerEntry) {
  if (!callbackIsPending) {
    window.digitalDataLayer.push(entry);
  }
  if (entry.event?.eventInfo?.callback) {
    callbackIsPending = true;
  }
}

const dataLayer = {
  pageLoaded(params: PageLoadedParameters) {
    // Coerce + validate in assertPayload; returns normalized values (loginStatus: '0' | '1' | undefined)
    const coercedParams = assertPayload.pageLoaded(
      params,
    ) as PageLoadedParameters;

    const normalizedLoginStatus = coercedParams.user?.loginStatus as
      | "0"
      | "1"
      | undefined;

    const pageLoadedObj: DataLayerEntry = {
      pageInstanceID: coercedParams.pageInstanceID,
      page: {
        pageInfo: {
          pageName: coercedParams.pageName,
          destinationURL: window.location.href,
          destinationURI: cleanCampaignQueryParams(
            coercedParams.destinationURI,
          ),
          referringURL: coercedParams.referrer ?? document.referrer,
          language: coercedParams.language,
          pageID: coercedParams.pageID,
          sysEnv: coercedParams.sysEnv,
        },
        category: {
          primaryCategory: coercedParams.primaryCategory,
        },
      },
      user: [
        {
          profile: [
            {
              profileInfo: {
                loginStatus: normalizedLoginStatus ?? "0",
                language:
                  normalizedLoginStatus === "1"
                    ? coercedParams.user?.language
                    : coercedParams.language,
                loginType: coercedParams.user?.loginType,
              },
            },
          ],
        },
      ],
    };
    if (coercedParams.search) {
      pageLoadedObj.search = getSearchDataLayerObject(coercedParams.search);
    }
    if (coercedParams.errorCode || coercedParams.errorMessage) {
      setPageAttributes(pageLoadedObj, {
        errorCode: coercedParams.errorCode,
        errorMessage: coercedParams.errorMessage,
      });
    }
    if (normalizedLoginStatus === "1") {
      setUserProfileInfo(pageLoadedObj, {
        UUID: coercedParams.user!.UUID,
        age: coercedParams.user!.age,
        gender: coercedParams.user!.gender,
        zipcode: coercedParams.user!.zipcode,
      });
    }
    pushToDataLayer(pageLoadedObj);
  },

  searchLoaded(params: SearchLoadedParameters) {
    assertPayload.searchLoaded(params);
    pushToDataLayer({ search: getSearchDataLayerObject(params) });
  },

  dataLayerReady() {
    propagateAdditionalPageLoadDetails();
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "page data layer ready",
        },
      },
    });
  },

  trackAccordionAction(params: TrackAccordionParameters) {
    assertPayload.accordion(params);

    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "click",
          eventType: "action",
          destination: "",
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          action: params.action,
          headline: params.headline ?? "",
        },
        category: {
          primaryCategory: "accordion",
        },
      },
    });
  },

  trackGenericNavigation(params: NavigationParameters) {
    assertPayload.genericNavigation(params);
    pushToDataLayer(createGenericNavigationEvent(params));
  },

  trackGenericClick(params: EventParameters) {
    assertPayload.genericClick(params);
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "click",
          eventType: "action",
          destination: "",
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          location: params.location,
          variant: params.variant,
          detail: params.detail,
        },
        category: {
          primaryCategory: "generic navigation",
        },
      },
    });
  },

  trackTeaserImpression(params: TrackTeaserImpressionParameters) {
    assertPayload.teaserImpression(params);
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "impression",
          eventType: "",
          destination: params.destination,
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          headline: params.headline,
          componentID: getComponentId(params),
          location: params.location,
          variant: params.variant,
          detail: params.detail,
          topic: params.topic,
          mustRead: params.mustRead,
          isAccessibleExternally: params.isAccessibleExternally,
          targetGroup: params.targetGroup,
          theme: params.theme,
          division: params.division,
          postType: params.postType,
          contentRegion: params.contentRegion,
        },
        category: {
          primaryCategory: "teaser",
        },
      },
    });
  },

  trackTeaserNavigation(params: TrackTeaserNavigationParameters) {
    assertPayload.teaserNavigation(params);
    const eventObject = createGenericNavigationEvent(params);
    setEventInfo(eventObject, {
      componentID: getComponentId(params),
      headline: params.headline,
      location: params.location,
      variant: params.variant,
      detail: params.detail,
      topic: params.topic,
      mustRead: params.mustRead,
      isAccessibleExternally: params.isAccessibleExternally,
      targetGroup: params.targetGroup,
      theme: params.theme,
      division: params.division,
      postType: params.postType,
      contentRegion: params.contentRegion,
    });
    setEventCategory(eventObject, { primaryCategory: "teaser" });
    pushToDataLayer(eventObject);
  },

  trackImpression(params: TrackImpressionParameters) {
    assertPayload.impression(params);

    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "impression",
          eventType: "",
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          headline: params.headline,
          // optional fields go INSIDE eventInfo:
          location: params.location,
          variant: params.variant,
          detail: params.detail,
        },
        category: {
          primaryCategory: params.type,
        },
      },
    });
  },

  trackDownload(params: TrackDownloadParameters) {
    assertPayload.download(params);
    const eventObject = createGenericNavigationEvent(params);
    setEventInfo(eventObject, {
      eventType: "download",
      value: params.filename,
    });
    setEventCategory(eventObject, { primaryCategory: "download" });
    pushToDataLayer(eventObject);
  },

  trackSearchResultNavigation(params: TrackSearchResultNavigationParameters) {
    assertPayload.searchResult(params);
    const eventObject = createGenericNavigationEvent(params);
    setEventInfo(eventObject, {
      action: `click on search result${params.type ? ` (${params.type})` : ""}`,
      value: params.value,
      resultPosition: params.position + 1,
      resultPageNumber: params.page ? params.page + 1 : 1,
    });
    setEventCategory(eventObject, {
      primaryCategory: `${params.level} search`,
    });
    pushToDataLayer(eventObject);
  },

  trackCustomNavigation(params: TrackCustomNavigationParameters) {
    assertPayload.customNavigation(params);
    const eventObject = createGenericNavigationEvent(params);
    if (params.primaryCategory !== undefined) {
      setEventCategory(eventObject, {
        primaryCategory: params.primaryCategory,
      });
    }
    if (params.action !== undefined) {
      setEventInfo(eventObject, { action: params.action });
    }
    pushToDataLayer(eventObject);
  },

  updateUserLoginType(params: UserLoginTypeParameters) {
    assertPayload.loginType(params);
    pushToDataLayer({
      user: [
        {
          profile: [
            {
              profileInfo: {
                loginType: params.loginType,
              },
            },
          ],
        },
      ],
    });
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "user update",
        },
      },
    });
  },

  sendCustomEvent(params: CustomEventParameters) {
    assertPayload.customEvent(params);
    const { primaryCategory, ...eventInfoProperties } = params;
    pushToDataLayer({
      event: {
        eventInfo: {
          ...eventInfoProperties,
        },
        category: {
          primaryCategory: primaryCategory,
        },
      },
    });
  },
};

export default dataLayer;

function getSearchDataLayerObject(params: {
  level: string;
  value: string;
  results: number;
}) {
  return {
    name: `${params.level} search`,
    term: params.value,
    results: params.results,
    resultPageNumber: params.results === 0 ? 0 : 1,
  };
}

function convertToComponentPath(path: Element | ComponentPath) {
  if (path instanceof Element) {
    return domComponentPath.get(path);
  }
  return path;
}

function getComponentId({
  headline,
  label,
  destination,
}: {
  headline: string;
  label: string;
  destination: string;
}) {
  return `${headline}/${label}/${destination}`;
}

function wrapCallback(callback: () => void) {
  function redirectCallback() {
    clearTimeout(timeoutId);
    callbackIsPending = false;
    callback();
  }

  const timeoutId = setTimeout(redirectCallback, REDIRECTION_TIMEOUT);

  return redirectCallback;
}

function createGenericNavigationEvent({
  callback,
  component,
  label,
  destination,
  location,
  variant,
  detail,
}: NavigationParameters) {
  let eventType = destination === "" ? "action" : "navigation";
  if (destination.startsWith("mailto:")) eventType = "mail";
  if (destination.startsWith("tel:")) eventType = "call";

  const eventObject: DataLayerEntry = {
    event: {
      eventInfo: {
        eventName: "click",
        eventType,
        destination,
        componentPath: convertToComponentPath(component),
        label,
        location,
        variant,
        detail,
      },
      category: {
        primaryCategory: "generic navigation",
      },
    },
  };

  if (eventType === "navigation" && callback) {
    setEventInfo(eventObject, { callback: wrapCallback(callback) });
  }
  return eventObject;
}

function cleanCampaignQueryParams(url: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  const search = anchor.search;

  if (!search) {
    return url;
  }

  const cleanedParams = search
    .slice(1)
    .split("&")
    .filter((param) => !param.startsWith("at_"))
    .join("&");
  const ie11LeadingSlashFix =
    url.startsWith("/") && !anchor.pathname.startsWith("/") ? "/" : "";
  if (!cleanedParams) {
    return `${ie11LeadingSlashFix}${anchor.pathname}`;
  }
  return `${ie11LeadingSlashFix}${anchor.pathname}?${cleanedParams}`;
}

function propagateAdditionalPageLoadDetails() {
  const element = querySelectorDeep("[data-sit-search-loaded]");
  if (element) {
    try {
      const parsedSearchLoaded = JSON.parse(
        element.dataset.sitSearchLoaded ?? "",
      );
      dataLayer.searchLoaded(parsedSearchLoaded);
    } catch (e) {
      console.error("Could not parse given data-sit-search-loaded: ", e);
    }
  }
}
